// @ts-nocheck
import React, { createContext, useContext, useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { createClient, Session, User } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { loadProfile, loadUserRoles, withRetry, clearAuthCaches } from '@/lib/authQueries';
import { resolveRleventsAccess } from '@/lib/rleventsAccess';
import {
  handleAuthFailure,
  logAuthDecision,
  refreshSessionWithRetry,
  recoverSessionFrom,
} from '@/lib/authSession';

const RECRUITLOOK_SUPABASE_URL = 'https://pvaumvowzncvrdkkvnqq.supabase.co';
const RECRUITLOOK_SUPABASE_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InB2YXVtdm93em5jdnJka2t2bnFxIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDU5MzkwNzYsImV4cCI6MjA2MTUxNTA3Nn0.tzzUBAW0sotu9taBdFv62G3RYDAnP7C5CPluRo4Odcs';

const recruitLookSupabase = createClient(RECRUITLOOK_SUPABASE_URL, RECRUITLOOK_SUPABASE_ANON_KEY, {
  auth: {
    storage: localStorage,
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
  },
});

export type Profile = {
  id: string;
  role: 'athlete' | 'parent' | 'club_coach' | 'college_coach' | 'coach_college' | 'affiliate' | 'vendor' | 'admin' | 'org_admin' | 'graphics' | 'press' | 'editor' | 'vendor_graphics' | 'vendor_press' | 'vendor_video' | 'scout';
  approval_status: 'pending' | 'approved' | 'rejected';
  name?: string;
  email?: string;
  username?: string;
  role_slug?: string;
  membership_id?: string;
  active_membership_slug?: string;
  user_id?: string;
  [key: string]: any; // For other profile fields
};

export type AuthContextType = {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  isLoading: boolean;
  loading: boolean; // Added loading property
  signIn: (email: string, password: string) => Promise<{
    success: boolean;
    error?: string;
  }>;
  signUp: (email: string, password: string, metadata?: any) => Promise<{
    success: boolean;
    error?: string;
  }>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>; // Adding a method to refresh the profile
  setProfile: (profile: Profile | null) => void; // Expose setProfile method
};

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  // Set just before a deliberate sign-out so the SIGNED_OUT log line can name
  // the real reason instead of guessing.
  const signOutReasonRef = useRef<any>(null);
  // The last session we held, kept so an unexpected SIGNED_OUT can be undone
  // with its refresh token instead of throwing the operator onto a login page.
  const lastSessionRef = useRef<any>(null);
  const recoveryInFlightRef = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Use React.lazy to avoid circular dependency issues with AdminImpersonationContext
  const getImpersonationContext = () => {
    try {
      // This will only be available if AdminImpersonationProvider is wrapping this component
      const AdminImpersonationContext = React.createContext<any>(undefined);
      return React.useContext(AdminImpersonationContext);
    } catch {
      return null;
    }
  };

  // Helper function to get dashboard path for role
  const getDashboardPathForRole = useCallback((roleSlug: string) => {
    const roleToDashboard = {
      'admin': '/admin-dashboard',
      'athlete': '/athlete-dashboard',
      'parent': '/parent-dashboard',
      'club_coach': '/club-dash',
      'college_coach': '/college-coach-dashboard',
      'affiliate': '/affiliate-dashboard',
      'vendor': '/vendor-dashboard',
      'press': '/vendor-press-dashboard',
      'vendor_press': '/vendor-dashboard',
      'sales': '/sales-dashboard'
    };
    return roleToDashboard[roleSlug as keyof typeof roleToDashboard];
  }, []);

  // Helper function to fetch membership dashboard path
  const fetchMembershipDashboardPath = useCallback(async (membershipSlug: string, dbClient = supabase) => {
    try {
      console.log("🔍 AuthContext: Fetching dashboard path for membership:", membershipSlug);
      
      const { data: membership, error } = await dbClient
        .from('memberships')
        .select('dashboard_path')
        .eq('slug', membershipSlug)
        .maybeSingle();
      
      if (error) {
        console.error('❌ AuthContext: Error fetching membership dashboard path:', error);
        toast.error(`Failed to load membership details for ${membershipSlug}`);
        return null;
      }
      
      if (!membership) {
        console.warn('⚠️ AuthContext: No membership found for slug:', membershipSlug);
        toast.warning(`Membership "${membershipSlug}" not found. Using default dashboard.`);
        return null;
      }
      
      console.log("✅ AuthContext: Found membership dashboard path:", membership.dashboard_path);
      return membership.dashboard_path;
    } catch (error) {
      console.error('❌ AuthContext: Exception fetching membership dashboard path:', error);
      return null;
    }
  }, []);

  // Fetch user profile data from profiles table - moved to a useCallback to prevent recreation
  const fetchUserProfile = useCallback(async (userId: string, dbClient = supabase) => {
    // CRITICAL: Skip all redirect logic for password reset pages
    // Use window.location.pathname (not React Router's location) to get current URL
    // React Router's location can be stale inside useCallback
    const currentPath = window.location.pathname;
    const isPasswordResetPage = currentPath === '/auth/reset-password' || 
                                 currentPath === '/reset-password';
    if (isPasswordResetPage) {
      console.log("🔐 AuthContext: On password reset page - skipping profile fetch redirects");
      setIsLoading(false);
      return;
    }

    // Signing in or creating an account mid-checkout must never navigate the
    // coach away from the registration wizard.
    const isCheckoutFlow = /\/register(\/|$)/.test(currentPath);
    
    
    try {
      console.log("🔍 AuthContext: Fetching profile for user ID:", userId);
      
      // Fetch profile data — shared cache + 3 retries with backoff.
      const useShared = dbClient === supabase;
      const profileOut = useShared
        ? await loadProfile(userId)
        : await withRetry('profiles', () =>
            dbClient.from('profiles').select('*').eq('user_id', userId).maybeSingle()
          );

      if (profileOut.status !== 'ok') {
        // Could not read. That is not "no profile" — keep whatever we have and
        // say nothing if the page simply navigated away.
        if (!profileOut.aborted) {
          console.warn('AuthContext: profile could not be read', profileOut.error);
          toast.error('Could not load your profile. Please retry.');
        }
        return;
      }

      const data: any = profileOut.data;

      if (data) {
        console.log("👤 AuthContext: Profile found:", data);

        // Authoritative role, from the same cached user_roles read everything
        // else uses.
        const rolesOut = useShared
          ? await loadUserRoles(userId)
          : await withRetry('user_roles', () =>
              dbClient.from('user_roles').select('role').eq('user_id', userId)
            );
        const roleList =
          rolesOut.status === 'ok'
            ? (Array.isArray(rolesOut.data)
                ? rolesOut.data.map((r: any) => (typeof r === 'string' ? r : r.role))
                : [])
            : [];

        if (roleList.length) {
          const primary = roleList[0];
          // Normalize rlevents_super_admin to admin so all admin checks work consistently
          data.role = primary === 'rlevents_super_admin' ? 'admin' : primary;
        } else {
          console.warn("⚠️ AuthContext: No role found in user_roles table, falling back to profiles.role");
        }
        
        console.log("🔍 AuthContext: Final role:", data.role);
        console.log("🔍 AuthContext: Profile role_slug:", data.role_slug);
        console.log("🔍 AuthContext: Profile active_membership_slug:", data.active_membership_slug);
        setProfile(data as Profile);
        console.log("✅ AuthContext: Profile fetch resolved");
        
        // Auto-link orphaned roster entries for athletes
        // This runs in the background to not slow down login
        if (data.email && (data.role === 'athlete' || !data.role)) {
          (async () => {
            try {
              console.log("🔗 AuthContext: Checking for orphaned roster entries for:", data.email);
              const { data: orphanedRosters, error: rosterError } = await dbClient
                .from('coach_team_rosters')
                .select('id')
                .eq('email', data.email.toLowerCase().trim())
                .is('profile_id', null);
              
              if (rosterError) {
                console.error('❌ AuthContext: Error checking orphaned rosters:', rosterError);
                return;
              }
              
              if (orphanedRosters && orphanedRosters.length > 0) {
                console.log(`🔗 AuthContext: Found ${orphanedRosters.length} orphaned roster entries, linking...`);
                const rosterIds = orphanedRosters.map(r => r.id);
                
                const { error: updateError } = await dbClient
                  .from('coach_team_rosters')
                  .update({ profile_id: data.id })
                  .in('id', rosterIds);
                
                if (updateError) {
                  console.error('❌ AuthContext: Error linking roster entries:', updateError);
                } else {
                  console.log(`✅ AuthContext: Successfully linked ${rosterIds.length} roster entries to profile`);
                }
              }
            } catch (err) {
              console.error('❌ AuthContext: Exception in roster auto-linking:', err);
            }
          })();
        }
        
        // Handle redirection based on approval status
        if (data.approval_status === 'pending' && !isCheckoutFlow) {
          console.log("⏳ AuthContext: User approval pending, redirecting to pending approval");
          navigate('/pending-approval');
          return;
        }
        
        if (data.approval_status === 'approved') {
          // Don't redirect if user is already on a valid path for their role
          const currentPath = location.pathname;
          console.log("✅ AuthContext: User approved, current path:", currentPath);
          
          // Only redirect if at login, root, or general dashboard paths
          // Don't redirect users who are on utility pages like billing, preferences, etc.
          const redirectPaths = [
            '/login', 
            '/', 
            '/dashboard',
            '/auth/login'
          ];
          const utilityPaths = [
            '/billing',
            '/preferences',
            '/profile-preferences',
            '/membership-success',
            '/packet-purchase-success'
          ];
          
          // Mobile app routes should NEVER trigger redirects
          const mobileAppPaths = ['/mobile-events'];
          const isMobileAppPath = mobileAppPaths.some(path => currentPath.startsWith(path));
          
          // Check for ?app= query parameter - these are mini-app routes that should not redirect
          const searchParams = new URLSearchParams(location.search);
          const hasAppParam = searchParams.has('app');
          
          const isRedirectPath = redirectPaths.includes(currentPath);
          const isUtilityPath = utilityPaths.some(path => currentPath.startsWith(path));
          const shouldRedirect =
            isRedirectPath && !isUtilityPath && !isMobileAppPath && !hasAppParam && !isCheckoutFlow;
          
          // ALWAYS allow billing page access - never redirect from it
          if (currentPath === '/billing' || currentPath.startsWith('/billing/')) {
            console.log("🔄 AuthContext: Billing page access - no redirect");
            return;
          }
          
          if (shouldRedirect) {
            console.log("🔄 AuthContext: Should redirect from:", currentPath);
            
            // RLevents access wins first: admins and event staff land on their
            // RLevents dashboard even when the profile role is a coach role
            // (many coaches also work events as staff). Only when the resolver
            // says this user is neither admin nor staff do the role-based
            // redirects below run.
            const access = await resolveRleventsAccess(user.id);
            if (access.status === 'ok' && (access.isAdmin || access.isStaff)) {
              navigate(access.landingPath, { replace: true });
              return;
            }

            // Hotel partners land on the Hotel Partner Portal. Users who also
            // hold an admin or staff role already returned above, so they keep
            // their dashboard and are not redirected.
            if (access.status === 'ok' && access.roles?.includes('hotel_partner')) {
              navigate('/hotel-portal', { replace: true });
              return;
            }

            // Handle club coach users specifically
            if (data.role === 'club_coach') {
              console.log("🏈 AuthContext: Club coach user detected, redirecting to club coach dashboard");
              navigate('/club-dash');
              return;
            }
            
            // Handle college coach users specifically - DIRECT REDIRECT
            if (data.role === 'college_coach' || data.role === 'coach_college') {
              console.log("🎓 AuthContext: College coach user detected, redirecting DIRECTLY to college coach dashboard");
              console.log("🎓 AuthContext: College coach profile data:", data);
              console.log("🎓 AuthContext: Bypassing Dashboard component completely");
              navigate('/college-coach-dashboard', { replace: true });
              return;
            }
            
            // Handle editor users specifically - DIRECT REDIRECT
            if (data.role === 'editor') {
              console.log("✏️ AuthContext: Editor user detected, redirecting DIRECTLY to editor dashboard");
              console.log("✏️ AuthContext: Editor profile data:", data);
              console.log("✏️ AuthContext: Current path:", currentPath);
              navigate('/editor', { replace: true });
              return;
            }
            
            // Handle parent users specifically
            if (data.role === 'parent') {
              console.log("👨‍👩‍👧‍👦 AuthContext: Parent user detected, redirecting to parent dashboard");
              navigate('/parent-dashboard');
              return;
            }
            
            // NEW LOGIC: Resolve dashboard path for athlete users
            let resolvedDashboardPath = null;
            
            // For athlete users, prioritize membership dashboard_path
            if (data.role_slug === 'athlete' || data.role === 'athlete') {
              console.log("🏃‍♂️ AuthContext: Processing athlete user dashboard resolution");
              
              // Check if user has active membership and fetch its dashboard_path
              if (data.active_membership_slug) {
                resolvedDashboardPath = await fetchMembershipDashboardPath(data.active_membership_slug, dbClient);
              }
              
              // Fallback to role-based dashboard path if membership lookup fails
              if (!resolvedDashboardPath) {
                resolvedDashboardPath = getDashboardPathForRole(data.role_slug || data.role);
                console.log("🎯 AuthContext: Using role-based dashboard_path:", resolvedDashboardPath);
              }
              
              // Final fallback for athletes
              if (!resolvedDashboardPath) {
                resolvedDashboardPath = '/athlete-dashboard';
                console.log("🎯 AuthContext: Using default athlete dashboard path");
              }
              
              console.log("🚀 AuthContext: Final resolved dashboard path for athlete:", resolvedDashboardPath);
              navigate(resolvedDashboardPath);
              return;
            }
            
            // For non-athlete users, use legacy role-based fallback.
            // The old `role_assignments` -> `roles` join was removed because
            // the `public.roles` table no longer exists in RLevents.
            const fallbackPath = getDashboardPathForRole(data.role_slug || data.role);
            if (fallbackPath) {
              console.log('🎯 AuthContext: Redirecting to fallback dashboard_path:', fallbackPath);
              if ((data.role_slug || data.role) === 'admin') {
                const isImpersonating = sessionStorage.getItem('admin_impersonation_state');
                if (!isImpersonating) {
                  navigate('/admin-dashboard');
                  return;
                }
                return;
              }
              navigate(fallbackPath);
              return;
            }

            // Fallback to legacy role-based redirect if no role assignments found
            console.log('⚠️ AuthContext: No role assignments found, using legacy role redirect for:', data.role);
            switch (data.role) {
              case 'athlete':
                navigate('/athlete-dashboard');
                break;
              case 'parent':
                navigate('/parent-dashboard');
                break;
              case 'club_coach':
                navigate('/club-dash');
                break;
              case 'college_coach':
                navigate('/college-coach-dashboard');
                break;
              case 'vendor':
                navigate('/vendor-dashboard');
                break;
              case 'affiliate':
                navigate('/affiliate-dashboard');
                break;
              case 'admin':
                const isImpersonating = sessionStorage.getItem('admin_impersonation_state');
                if (!isImpersonating) {
                  console.log('👑 AuthContext: Legacy admin redirect to admin dashboard');
                  navigate('/admin-dashboard');
                } else {
                  console.log('👑 AuthContext: Admin is impersonating, allowing current navigation');
                }
                break;
              case 'org_admin':
                console.log('🏢 AuthContext: Org admin redirect to org admin dashboard');
                navigate('/org-admin-dashboard');
                break;
              case 'press':
                console.log('📰 AuthContext: Press user redirect to vendor-press-dashboard');
                navigate('/vendor-press-dashboard');
                break;
              case 'vendor_graphics':
                console.log('🎨 AuthContext: Vendor graphics user redirect to vendor-graphics-dashboard');
                navigate('/vendor-graphics-dashboard');
                break;
              case 'sales':
                console.log('💼 AuthContext: Sales user redirect to sales dashboard');
                navigate('/sales-dashboard');
                break;
              case 'editor':
                console.log('✏️ AuthContext: Editor user redirect to editor dashboard');
                navigate('/editor');
                break;
              default:
                navigate('/athlete-dashboard');
            }
          }
        }
      } else {
        console.error("❌ AuthContext: No profile found for user ID:", userId);
        // Don't show error toast for new registrations, they might still be processing
        console.log("⏳ AuthContext: Profile might still be creating, will retry...");
      }
    } catch (error: any) {
      console.error('❌ AuthContext: Error fetching user profile:', error);
      toast.error('Failed to load user profile');
    } finally {
      setIsLoading(false);
      console.log("✅ AuthContext: Loading flipped false");
    }
  }, [navigate, location.pathname, getDashboardPathForRole, fetchMembershipDashboardPath]);

  // Function to refresh the user profile
  const refreshProfile = useCallback(async () => {
    if (!user) return;
    
    try {
      setIsLoading(true);
      await fetchUserProfile(user.id);
    } catch (error) {
      console.error('Error refreshing profile:', error);
    } finally {
      setIsLoading(false);
    }
  }, [user, fetchUserProfile]);

  // Initialize auth state
  useEffect(() => {
    let subscription: { data: { subscription: { unsubscribe: () => void } } } | null = null;
    let isInitialized = false;
    
    // Set up auth state listener only once
    const setupAuthListener = async () => {
      if (isInitialized) return;
      isInitialized = true;
      const isClaimedAthleteDashboard =
        window.location.pathname.startsWith('/athlete-dashboard') &&
        new URLSearchParams(window.location.search).get('claimed') === '1';
      const activeAuthClient = isClaimedAthleteDashboard ? recruitLookSupabase : supabase;
      
      // SIGNED_IN / INITIAL_SESSION can fire several times per load. Debounce
      // so a load produces ONE profile+role fetch, not three.
      let sessionFetchTimer: any = null;
      // One profile+role fetch per signed-in account per load. The first-load
      // path below and the listener both arrive here; the second must not
      // repeat the work (and the renders) the first one already did.
      let fetchedForUser: string | null = null;
      const runProfileFetch = (uid: string) => {
        if (fetchedForUser === uid) return;
        fetchedForUser = uid;
        return fetchUserProfile(uid, activeAuthClient);
      };
      const scheduleProfileFetch = (uid: string) => {
        if (fetchedForUser === uid) return;
        if (sessionFetchTimer) clearTimeout(sessionFetchTimer);
        sessionFetchTimer = setTimeout(() => {
          sessionFetchTimer = null;
          runProfileFetch(uid);
        }, 120);
      };
      // Repeated SIGNED_IN / INITIAL_SESSION events carry the same session.
      // Writing it again renders the whole tree for nothing.
      const applySession = (sessionData: any) => {
        if (sessionData?.refresh_token) lastSessionRef.current = sessionData;
        setSession((prev: any) =>
          prev?.access_token === sessionData?.access_token ? prev : sessionData
        );
        setUser((prev: any) =>
          prev?.id === sessionData?.user?.id ? prev : (sessionData?.user ?? null)
        );
      };

      // The only place anyone is actually ejected.
      const ejectToLogin = () => {
        if (sessionFetchTimer) clearTimeout(sessionFetchTimer);
        fetchedForUser = null;
        lastSessionRef.current = null;
        clearAuthCaches();
        setSession(null);
        setUser(null);
        setProfile(null);

        // CRITICAL: Use window.location.pathname (not React Router's location)
        // because this callback is created once at mount time and location is stale
        const currentPath = window.location.pathname;
        const isPasswordResetPath = currentPath === '/auth/reset-password' ||
                                    currentPath === '/reset-password';
        if (isPasswordResetPath) {
          console.log('🔐 AuthContext: On password reset page - NOT redirecting on SIGNED_OUT');
          return;
        }

        // Don't navigate on initial load
        if (currentPath !== '/') {
          navigate('/auth/login');
        }
      };

      // Set up auth state listener first
      subscription = activeAuthClient.auth.onAuthStateChange(
        (event, sessionData) => {
          console.log('Auth state changed:', event);

          if (event === 'SIGNED_IN' && sessionData?.user) {
            recoveryInFlightRef.current = false;
            applySession(sessionData);
            scheduleProfileFetch(sessionData.user.id);
          } else if (event === 'SIGNED_OUT') {
            const deliberate = signOutReasonRef.current === 'user_clicked_sign_out';
            const reason = signOutReasonRef.current;
            signOutReasonRef.current = null;

            // An unexpected SIGNED_OUT is not proof the session is gone.
            // supabase-js also ends a session locally after a 403 bad_jwt or a
            // 5xx it could not interpret. Try once to put it back.
            if (!deliberate && lastSessionRef.current?.refresh_token && !recoveryInFlightRef.current) {
              recoveryInFlightRef.current = true;
              const last = lastSessionRef.current;
              console.warn(
                '[auth-exit] HOLD (unexpected sign-out — attempting to restore the session) | at=AuthContext onAuthStateChange SIGNED_OUT'
              );
              void (async () => {
                const { outcome, session: restored, error } = await recoverSessionFrom(
                  activeAuthClient,
                  last
                );
                recoveryInFlightRef.current = false;
                if (outcome === 'recovered') {
                  logAuthDecision('unknown', 'AuthContext SIGNED_OUT recovery (session restored)');
                  applySession(restored);
                  if (restored?.user?.id) runProfileFetch(restored.user.id);
                  return;
                }
                if (outcome === 'unknown') {
                  // Nothing is known. Keep the operator where they are.
                  logAuthDecision('unknown', 'AuthContext SIGNED_OUT recovery (no answer)', error);
                  return;
                }
                logAuthDecision('session_gone', 'AuthContext SIGNED_OUT recovery (session really gone)', error);
                ejectToLogin();
              })();
              return;
            }

            if (!deliberate) {
              logAuthDecision(
                reason ?? 'session_gone',
                'AuthContext onAuthStateChange SIGNED_OUT'
              );
            }
            ejectToLogin();
          } else if (event === 'TOKEN_REFRESHED' && sessionData) {
            // Only update session on token refresh, don't trigger profile refetch
            applySession(sessionData);
          } else if (event === 'INITIAL_SESSION' && sessionData?.user) {
            applySession(sessionData);
            scheduleProfileFetch(sessionData.user.id);
          }
        }
      );

      // Then check for existing session. getSession() reads storage (and, on a
      // preview surface, waits for the postMessage broker). It never calls
      // GET /user, so it can never 403 on the anon key.
      try {
        setIsLoading(true);
        const { data: { session: currentSession }, error: sessionError } =
          await activeAuthClient.auth.getSession();

        if (sessionError) {
          // Do not eject on an unknown failure. handleAuthFailure decides.
          handleAuthFailure(sessionError, 'AuthContext init getSession', {
            signOut: async () => {
              signOutReasonRef.current = 'session_gone';
              await activeAuthClient.auth.signOut();
            },
          });
        }

        let usableSession = currentSession;

        // An expired token in storage is not a logout. Refresh it, retrying
        // through 5xx/network trouble before anyone is sent to a login screen.
        const expired =
          currentSession?.expires_at && currentSession.expires_at * 1000 <= Date.now();
        if (expired) {
          const refreshed = await refreshSessionWithRetry(activeAuthClient);
          if (refreshed.status === 'ok') {
            usableSession = refreshed.session;
          } else if (refreshed.status === 'failed') {
            handleAuthFailure(refreshed.error, 'AuthContext init refresh', {
              signOut: async () => {
                signOutReasonRef.current = refreshed.kind;
                await activeAuthClient.auth.signOut();
              },
            });
            if (refreshed.kind === 'unknown') usableSession = currentSession;
            else usableSession = null;
          }
        }

        if (usableSession) {
          applySession(usableSession);
        } else {
          setSession(null);
          setUser(null);
        }

        if (usableSession?.user) {
          await runProfileFetch(usableSession.user.id);
        }
      } catch (error) {
        // Unknown: a thrown network error here must not look like a logout.
        handleAuthFailure(error, 'AuthContext init');
      } finally {
        setIsLoading(false);
      }
    };

    setupAuthListener();

    // Clean up subscription on component unmount
    return () => {
      if (subscription?.data?.subscription) {
        subscription.data.subscription.unsubscribe();
      }
    };
  }, []); // Empty dependency array to prevent re-runs

  // SSO bridge constants
  const SUPABASE_URL = "https://kvttsygfutwcywroraji.supabase.co";
  const SUPABASE_ANON_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Imt2dHRzeWdmdXR3Y3l3cm9yYWppIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzA4MTU1OTUsImV4cCI6MjA4NjM5MTU5NX0.wUaZcZxKQpyk2DTzTF2CjYHvWRHa897xW0rleO6mJos";

  // Sign in via SSO bridge (RecruitLook is source of truth, mirrors to RLevents)
  const signIn = async (email: string, password: string) => {
    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/sso-sign-in`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "apikey": SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({ email, password }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        const msg = err.error ?? "Sign-in failed";
        toast.error(msg);
        return { success: false, error: msg };
      }

      const { hashed_token, type } = await res.json();
      const { error } = await supabase.auth.verifyOtp({ token_hash: hashed_token, type });
      if (error) {
        toast.error(error.message);
        return { success: false, error: error.message };
      }

      toast.success('Signed in successfully');
      return { success: true };
    } catch (error: any) {
      toast.error(error.message || 'Failed to sign in');
      return { success: false, error: error.message };
    }
  };

  // Sign up via SSO bridge — creates user in RecruitLook, mirrors to RLevents with same UUID
  const signUp = async (email: string, password: string, metadata?: any) => {
    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/sso-sign-up`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "apikey": SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({
          email,
          password,
          first_name: metadata?.first_name,
          last_name: metadata?.last_name,
          phone: metadata?.phone,
        }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        const msg = err.error ?? "Sign-up failed";
        toast.error(msg);
        return { success: false, error: msg };
      }

      const { hashed_token, type } = await res.json();
      const { error } = await supabase.auth.verifyOtp({ token_hash: hashed_token, type });
      if (error) {
        toast.error(error.message);
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error: any) {
      toast.error(error.message || 'Failed to sign up');
      return { success: false, error: error.message };
    }
  };

  // Sign out function
  const signOut = async () => {
    signOutReasonRef.current = 'user_clicked_sign_out';
    console.info('[auth-exit] SIGN OUT (user clicked sign out) | reason=user_action | at=AuthContext.signOut');
    clearAuthCaches();
    await supabase.auth.signOut();
    setUser(null);
    setProfile(null);
    navigate('/auth/login');
  };

  // Check if we should use impersonated data - This will be properly handled by a hook
  const getEffectiveUserData = () => {
    // Simple pass-through for now - the impersonation will be handled at the UI level
    return {
      effectiveUser: user,
      effectiveProfile: profile
    };
  };

  const { effectiveUser, effectiveProfile } = getEffectiveUserData();

  // A fresh object here re-renders every consumer of this context on every
  // render of the provider. Keep one identity until something really changes.
  const value = useMemo(
    () => ({
      user: effectiveUser,
      session,
      profile: effectiveProfile,
      isLoading,
      loading: isLoading, // Add the loading property alias
      signIn,
      signUp,
      signOut,
      refreshProfile,
      setProfile, // Expose setProfile method
    }),
    [effectiveUser, session, effectiveProfile, isLoading, refreshProfile]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
